<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import { DEFAULT_LOCALE, type Locale } from '@/i18n'
import { usePaymentLink } from '@/composables/usePaymentLink'

const { t, locale } = useI18n()
const router = useRouter()
const { url, submitting, error, hasPending, generate } = usePaymentLink()

const currentLocale = computed<Locale>(() => (locale.value === 'en' ? 'en' : DEFAULT_LOCALE))
const homeHref = computed(() => (currentLocale.value === DEFAULT_LOCALE ? '/' : `/${currentLocale.value}/`))

useHead({ title: computed(() => `${t('paymentLink.title')} — MySmerra`) })

/** loading (generating or not started yet) · error · success. */
const view = computed<'loading' | 'error' | 'success'>(() =>
  url.value ? 'success' : error.value ? 'error' : 'loading',
)

/**
 * Generate the link from the context staged on the recap step. A page reached
 * without that context (hard reload / direct visit) has nothing to generate:
 * send the user back home rather than show a dead loading state.
 */
onMounted(() => {
  if (!hasPending() && !url.value && !submitting.value) {
    void router.replace(homeHref.value)
    return
  }
  void generate()
})

/* --- Copie du lien dans le presse-papier, avec repli. --- */
const inputRef = ref<HTMLInputElement | null>(null)
const copied = ref(false)
let copiedTimer: ReturnType<typeof setTimeout> | undefined

function flagCopied() {
  copied.value = true
  if (copiedTimer) clearTimeout(copiedTimer)
  copiedTimer = setTimeout(() => {
    copied.value = false
  }, 2000)
}

/** Repli pour les navigateurs sans `navigator.clipboard` (ou contexte non sécurisé). */
function legacyCopy(text: string): boolean {
  if (typeof document === 'undefined') return false
  const el = document.createElement('textarea')
  el.value = text
  el.setAttribute('readonly', '')
  el.style.position = 'absolute'
  el.style.left = '-9999px'
  document.body.appendChild(el)
  el.select()
  let ok = false
  try {
    ok = document.execCommand('copy')
  } catch {
    ok = false
  }
  document.body.removeChild(el)
  return ok
}

async function copyLink() {
  if (!url.value) return
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url.value)
      flagCopied()
      return
    }
    if (legacyCopy(url.value)) flagCopied()
    else inputRef.value?.select()
  } catch {
    // Dernier recours : on sélectionne le champ pour une copie manuelle.
    inputRef.value?.select()
  }
}

onBeforeUnmount(() => {
  if (copiedTimer) clearTimeout(copiedTimer)
})
</script>

<template>
  <section class="pl" aria-labelledby="pl-title">
    <div class="pl__card">
      <!-- État : génération en cours -->
      <template v-if="view === 'loading'">
        <div class="pl__icon pl__icon--loading" aria-hidden="true">
          <span class="pl__spinner" />
        </div>
        <p class="pl__loading" role="status">{{ t('paymentLink.loading') }}</p>
      </template>

      <!-- État : erreur -->
      <template v-else-if="view === 'error'">
        <div class="pl__icon pl__icon--error" aria-hidden="true">
          <svg viewBox="0 0 32 32" width="40" height="40">
            <circle cx="16" cy="16" r="14" fill="none" stroke="currentColor" stroke-width="2" />
            <path d="M16 9v9" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" />
            <circle cx="16" cy="22.5" r="1.4" fill="currentColor" />
          </svg>
        </div>
        <!-- L'erreur ici ne concerne que la génération du lien (le contract_draft
             est déjà créé côté récap) : message générique via paymentLink.errors.*. -->
        <p class="pl__error" role="alert">{{ t(error as string) }}</p>
        <a :href="homeHref" class="pl__home">{{ t('paymentLink.backHome') }}</a>
      </template>

      <!-- État : lien généré (succès) -->
      <template v-else>
        <div class="pl__icon" aria-hidden="true">
          <svg viewBox="0 0 32 32" width="40" height="40">
            <circle cx="16" cy="16" r="14" fill="none" stroke="currentColor" stroke-width="2" />
            <path d="M10 16l4 4 8-9" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>
        <h1 id="pl-title" class="pl__title">{{ t('paymentLink.title') }}</h1>
        <p class="pl__subtitle">{{ t('paymentLink.subtitle') }}</p>

        <div class="pl__field">
          <label class="pl__field-label sr-only" for="pl-url">{{ t('paymentLink.linkLabel') }}</label>
          <input
            id="pl-url"
            ref="inputRef"
            class="pl__input"
            type="text"
            :value="url"
            readonly
            spellcheck="false"
            @focus="($event.target as HTMLInputElement).select()"
          />
          <button
            type="button"
            class="pl__copy-icon"
            :aria-label="t('paymentLink.copyAria')"
            @click="copyLink"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <rect x="9" y="9" width="11" height="11" rx="2" fill="none" stroke="currentColor" stroke-width="1.8" />
              <path d="M5 15V5a2 2 0 0 1 2-2h8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
            </svg>
          </button>
        </div>

        <button type="button" class="pl__copy" @click="copyLink">
          {{ copied ? t('paymentLink.copied') : t('paymentLink.copy') }}
        </button>

        <p class="pl__sign">
          {{ t('paymentLink.signOff') }}<br />
          {{ t('paymentLink.team') }}
        </p>
        <a :href="homeHref" class="pl__home">{{ t('paymentLink.backHome') }}</a>
      </template>
    </div>
  </section>
</template>

<style scoped>
.pl {
  min-height: 60vh;
  display: grid;
  place-items: center;
  padding: clamp(2rem, 6vw, 4rem) var(--layout-gutter);
}
.pl__card {
  width: 100%;
  max-width: 40.5rem; /* maquette : 648px */
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1rem;
  /* Padding maquette (34 / ~74 / 66px) plafonné puis réduit sur mobile. */
  padding: clamp(1.5rem, 4vw, 2.125rem) clamp(1.25rem, 6vw, 4.625rem) clamp(2rem, 5vw, 4.125rem);
  background: var(--color-white);
  border-radius: 1.25rem; /* maquette : 20px */
  border: 1px solid var(--border);
  box-shadow: var(--shadow-md);
}
.pl__icon {
  display: grid;
  place-items: center;
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: color-mix(in oklab, var(--color-peach) 18%, white);
  color: var(--primary);
}
.pl__icon--error {
  background: color-mix(in oklab, var(--color-cherry) 12%, white);
  color: var(--color-cherry);
}
.pl__icon--loading {
  background: color-mix(in oklab, var(--color-peach) 18%, white);
}
.pl__spinner {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  border: 3px solid color-mix(in oklab, var(--primary) 30%, transparent);
  border-top-color: var(--primary);
  animation: pl-spin 0.8s linear infinite;
}
@keyframes pl-spin {
  to { transform: rotate(360deg); }
}
@media (prefers-reduced-motion: reduce) {
  .pl__spinner { animation-duration: 2s; }
}
.pl__loading {
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--text-muted);
}
.pl__title {
  font-size: clamp(1.625rem, 5vw, 2rem); /* maquette : 32px, fluide sur mobile */
  font-weight: 700;
  line-height: 1.1; /* maquette : 100% */
  color: var(--color-chocolate);
  max-width: 30rem;
  text-wrap: balance; /* deux lignes équilibrées comme la maquette */
}
.pl__subtitle {
  font-size: clamp(1.125rem, 4vw, 1.5rem); /* maquette : 24px, fluide sur mobile */
  font-weight: 500;
  line-height: 1.2;
  color: var(--color-chocolate);
  max-width: 30rem;
  text-wrap: balance;
}

/* Champ lien (encadré) + bouton icône de copie séparé à droite (maquette). */
.pl__field {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  max-width: 28rem;
  margin-top: 0.2rem;
}
.pl__input {
  flex: 1 1 auto;
  min-width: 0;
  padding: 0.7rem 0.9rem;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  background: var(--color-white);
  font: inherit;
  font-size: var(--text-sm);
  color: var(--color-chocolate);
  text-overflow: ellipsis;
}
.pl__input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--focus-ring);
}
.pl__copy-icon {
  flex-shrink: 0;
  display: grid;
  place-items: center;
  padding: 0.4rem;
  border: 0;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--text-muted);
  cursor: pointer;
  transition: color var(--dur-fast) var(--ease-out), background var(--dur-fast) var(--ease-out);
}
.pl__copy-icon:hover { color: var(--color-chocolate); background: var(--bg-soft); }
.pl__copy-icon:focus-visible { outline: 2px solid var(--focus-ring); outline-offset: 2px; }

/* Bouton principal « Copier le lien » */
.pl__copy {
  margin-top: 0.2rem;
  min-width: 12rem;
  padding: 0.8rem 1.6rem;
  border: 0;
  border-radius: var(--radius-md);
  background: var(--primary);
  color: var(--text-on-primary);
  font-size: var(--text-sm);
  font-weight: 700;
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-out), transform var(--dur-base) var(--ease-spring);
}
.pl__copy:hover { background: var(--primary-hover); transform: translateY(-1px); }
.pl__copy:focus-visible { outline: 2px solid var(--focus-ring); outline-offset: 2px; }

.pl__sign {
  margin-top: 0.4rem;
  font-size: var(--text-sm);
  line-height: 1.5;
  color: var(--text-muted);
  font-style: italic;
}
.pl__error {
  font-size: var(--text-base);
  line-height: var(--leading-normal);
  color: var(--color-chocolate);
  max-width: 32ch;
}
.pl__home {
  margin-top: 0.4rem;
  display: inline-flex;
  align-items: center;
  padding: 0.7rem 1.6rem;
  background: var(--primary);
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--text-on-primary);
  text-decoration: none;
  transition: background var(--dur-fast) var(--ease-out), transform var(--dur-base) var(--ease-spring);
}
.pl__home:hover { background: var(--primary-hover); transform: translateY(-1px); }
.pl__home:focus-visible { outline: 2px solid var(--focus-ring); outline-offset: 2px; }

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
